import React from 'react';

import { useApolloClient } from '@apollo/client';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  GetQueueRecommendationsDocument,
  GetQueueRecommendationsQuery,
} from 'graphql/generated/graphql';

import { usePlayer } from 'hooks/usePlayer';

import {
  getNextStoryToPlay,
  getPreviousStoryToPlay,
  playAudioAndClearHistory,
  playAudioFromQueue,
  playAudioFromRecommendations,
  playNext,
  playPrevious,
  QueueState,
  QueueStory,
  setRecommendations,
} from 'store/modules/queue';
import ReduxState from 'store/ReduxState';

import useGraphaelDefaults from './useGraphaelDefaults';

export enum PlayNextResult {
  NoStoryToPlay,
  NextStoryPlayed,
}

export const useQueue = () => {
  const dispatch = useDispatch();

  const { playStoryAudio, autoPlay } = usePlayer();

  const campaignArn = useSelector<ReduxState, string | undefined>(
    ({ recommendationsCampaigns }) =>
      recommendationsCampaigns['player.similarAudios'],
  );

  const queue = useSelector<ReduxState, QueueState>(
    ({ queue }) => queue,
    shallowEqual,
  );

  const previousStoryToPlay = useSelector(getPreviousStoryToPlay);
  const nextStoryToPlay = useSelector(getNextStoryToPlay);

  const hasPrevious = React.useCallback(() => {
    return previousStoryToPlay !== undefined;
  }, [previousStoryToPlay]);

  const hasNext = React.useCallback(() => {
    return nextStoryToPlay !== undefined;
  }, [nextStoryToPlay]);

  const hasStories = React.useCallback(() => {
    return (
      queue.queue.length + queue.history.length + queue.recommendations.length >
      0
    );
  }, [queue.history.length, queue.queue.length, queue.recommendations.length]);

  const apolloClient = useApolloClient();
  const defaults = useGraphaelDefaults();

  const initQueueWithStory = React.useCallback(
    async (story: QueueStory) => {
      dispatch(playAudioAndClearHistory(story));

      if (campaignArn && autoPlay) {
        const { data } = await apolloClient.query<GetQueueRecommendationsQuery>(
          {
            ...defaults,
            query: GetQueueRecommendationsDocument,
            variables: {
              campaign: campaignArn,
              storyId: story.id,
            },
          },
        );

        if (data) {
          const recommendedStories =
            data?.recommendedStories?.recommendedStories;

          if (recommendedStories && recommendedStories.length > 0) {
            dispatch(setRecommendations(recommendedStories));
          }
        }
      }
    },
    [apolloClient, autoPlay, campaignArn, defaults, dispatch],
  );

  const playPreviousStory = React.useCallback(() => {
    if (previousStoryToPlay) {
      playStoryAudio(previousStoryToPlay);
      dispatch(playPrevious());
    }
  }, [dispatch, playStoryAudio, previousStoryToPlay]);

  const playNextStory = React.useCallback((): PlayNextResult => {
    if (nextStoryToPlay) {
      playStoryAudio(nextStoryToPlay);
      dispatch(playNext());

      return PlayNextResult.NextStoryPlayed;
    }

    return PlayNextResult.NoStoryToPlay;
  }, [dispatch, nextStoryToPlay, playStoryAudio]);

  const playFromQueue = React.useCallback(
    (index: number) => {
      const story = queue.queue[index];

      if (story) {
        playStoryAudio(story);
        dispatch(playAudioFromQueue(index));
      }
    },
    [dispatch, playStoryAudio, queue.queue],
  );

  const playFromRecommendations = React.useCallback(
    (index: number) => {
      const story = queue.recommendations[index];

      if (story) {
        playStoryAudio(story);
        dispatch(playAudioFromRecommendations(index));
      }
    },
    [dispatch, playStoryAudio, queue.recommendations],
  );

  return {
    hasPrevious,
    hasNext,
    hasStories,
    playPreviousStory,
    playNextStory,
    initQueueWithStory,
    playFromQueue,
    playFromRecommendations,
  };
};
